/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Dashboard
 * (C) Copyright IBM Corp. 2020, 2025
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../widgets/livewidget/nls/StringResources',
	'react',
	'underscore',
	'@ba/ba-ui-carbon-toolkit',
	'../grid/GridDropTarget',
	'../../../util/DashboardFormatter',
	'./helper/GridCellColRowSpanHelper',
	'./CrosstabConstants'
], (
	StringResources,
	React,
	_,
	Toolkit,
	GridDropTarget,
	Formatter,
	GridCellColRowSpanHelper,
	Constants
) => {
	'use strict';

	const JUSTIFY_CONTENT_MAP = {
		Right: 'flex-end',
		Left: 'flex-start',
		Center: 'center'
	};

	//NOTE: The crosstab column headers are fixed row headers from a grid control perspective - they don't scroll down
	class CrosstabCellRenderers {
		/**
         *
         * @param {object} options
         * @param {object} options.gridInfo an object that has the info of the whole grid
         * @param {int} options.gridInfo.fixedRows number of fixed rows of a grid
         * @param {int} options.gridInfo.fixedColumns number of fixed columns of a grid
         * @param {int} options.gridInfo.numRows number of rows of a grid
         * @param {int} options.gridInfo.numColumns number of columns of a grid
         * @param {int} options.gridInfo.fixedColumnWidth the default width of a fixed cell
         * @param {int} options.gridInfo.fixedRowHeight the default height of a fixed cell
         * @param {Set} options.gridInfo.coveredCellSet the set that all of its elements are cells covered by other
         *                                              cells, which means its elements don't need to be rendered
         * @param {boolean} options.gridInfo.isSuppressed true when a grid is toggled as suppressed
         * @param {Map} options.gridInfo.expandedRootMap a map whose keys are the root of expansions, values are arrays
         *                                                 of its last direct or indirect expansions leaf. We don't need
         *                                                 to know the expansions leaves therefore this map may be a set.
         *                                                  key: cellInfo.currentCellKey
         *                                                  value: [cellInfo.currentCellKey]
         * @param {Map} options.gridInfo.expandedLeafMap it's a reverse of expandedRootMap. The keys are leaves of
         *                                                  expansions and their values are an array of the direct
         *                                                  or indirect roots of an expansion leaf.
         *                                                  key: cellInfo.currentCellkey
         *                                                  value: [...JSON.stringfy({ cellKey: cellInfo.currentCellKey, resizeKey: cellInfo.resizeKey })]
         * @param {Set} options.gridInfo.renderedExpandedRootSet a set contains the root of expansions that has been rendered. Due to
         *                                                       the limitation of react-virtualized, only the cells within the current
         *                                                       visual ranges are rendered to improve the performance. However, when an
         *                                                       expansion is very long, its root maybe out of the visual range while its
         *                                                       leaves are in the range. But we still want to render the root. Therefore,
         *                                                       we use this set to check if the root is rendered.
         * @param {Map} options.gridInfo.cellProps a map that stores the cell properties passed from the callers of a (especially) root cell. Then, the
         *                                          stored cell props will be used to render a root cell that is out of the visual range.
         * @param {object} options.gridInfo.crosstabGrid an instance of the CrosstabGrid class. We have to use it for now but it should be gone later.
         * @param {object} options.gridInfo.dataProvider provides the data of a pair of given row and column index
         */
		static getFixedCellRenderer(options){
			const gridInfo = options.gridInfo;
			const crosstabGrid = gridInfo.crosstabGrid;
			const isRowEdge = options.isRowEdge;
			const gridCellSelection = options.gridCellSelection;

			// eslint-disable-next-line react/display-name
			return function(rowIndex, colIndex, gridCellProps) {
				const renderInfo = CrosstabCellRenderers._getRenderInfo({ rowIndex, colIndex, gridInfo, isRowEdge });

				if(gridInfo.coveredCellSet.has(renderInfo.cellKey)){
					return null;
				}else{
					const cellInfo = CrosstabCellRenderers.captureFixedCellInfo(rowIndex, colIndex, gridCellProps, gridInfo, isRowEdge);

					//populate the attributes we needed
					Object.assign(gridCellProps, {
						useValue: cellInfo.useValue,
						nestlevel: cellInfo.nestLevel,
						row: cellInfo.currentRow,
						col: cellInfo.currentCol,
						scope: cellInfo.scope,
						'aria-label': cellInfo.dataCellValue,
						tabindex: Constants.TABINDEX,
						area: cellInfo.area,
						measure: gridCellProps.isMeasure
					});

					if(renderInfo.isHiddenLane(cellInfo)){
						gridCellProps.style.zIndex = renderInfo.hiddenZIndex;
						delete gridCellProps.style[renderInfo.deleteSize];
						return React.createElement( 'div', gridCellProps, CrosstabCellRenderers.renderHiddenRowsAndColumnsHandler(cellInfo.useValue, renderInfo.hiddenLanePosition, gridInfo));

					} else {
						const classList = CrosstabCellRenderers._buildFixedSectionClassList(cellInfo, gridInfo, gridCellProps, isRowEdge);

						//Text toolbar style applied
						if(cellInfo.valueType) {
							const styleIndexForValueType = Constants.TEXT_STYLE_INDEX[cellInfo.valueType];
							Object.assign(gridCellProps.style, crosstabGrid.textStyle[styleIndexForValueType]);
						}

						//Selection formatting for text toolbar
						const selectedAreaStyles = crosstabGrid._getSelectionFormattingStyles(cellInfo.useValue);
						if(selectedAreaStyles){
							Object.assign(gridCellProps.style, selectedAreaStyles.convertedStylesValue);
						}

						const children = [CrosstabCellRenderers.renderTextCellOfFixedCell(cellInfo, gridInfo, isRowEdge)];

						// styles overwritten for summary cell
						if (cellInfo.isSummary ){
							gridCellProps.area = Constants.AREA_TYPE.SUMMARY;
							Object.assign(gridCellProps.style, crosstabGrid.textStyle[Constants.TEXT_STYLE_INDEX.SUMMARY]);
							CrosstabCellRenderers.setSummaryColor(gridCellProps, gridInfo);
							// Set the text alignment of the text child
							children[0].props.style.justifyContent = JUSTIFY_CONTENT_MAP[gridCellProps.style['text-align']];
						} else {
							gridCellSelection.addCellIsFocusedClass({ gridCellProps, classList });
							// Set the text alignment of the text child
							children[0].props.style.justifyContent = JUSTIFY_CONTENT_MAP[gridCellProps.style['text-align']];
							//render selection and implied Selection
							crosstabGrid._renderEdgeSelection(cellInfo.valueType, cellInfo.dataValue, cellInfo.useValue, classList);

							if (gridInfo.gridDnDManager) {
								CrosstabCellRenderers._renderGridDropZones({ children, isRowEdge, cellInfo, gridInfo });
							}
						}

						const missingExpandedRoot = CrosstabCellRenderers._renderExpand({ cellInfo, gridInfo, gridCellProps, isRowEdge, isInVisibleRange: renderInfo.isInVisibleRange, getOriginalCoords: renderInfo.getOriginalCoords, gridCellSelection: gridCellSelection });

						// clean up memory
						if (renderInfo.isOutVisibleRange(cellInfo, gridInfo)){
							gridInfo.renderedExpandedRootSet = new Set();
						}

						gridCellProps.className = classList.join(' ');

						if (gridInfo.gridDnDManager) {
							CrosstabCellRenderers._addResizeHandlers({ children, cellInfo, gridInfo, renderInfo });
						}
						const container = gridInfo.gridDnDManager ? GridDropTarget : 'div';
						const fixedRowCell =  React.createElement(
							container,
							{ dndManager: gridInfo.gridDnDManager, cellType: cellInfo.cellType, isMeasure: cellInfo.isMeasure, ...gridCellProps },
							children
						);

						const element = React.createElement(React.Fragment, {}, [...missingExpandedRoot, fixedRowCell]);
						crosstabGrid.addSpanInfo({ rowSpan: cellInfo.rowSpan, colSpan: cellInfo.colSpan }, gridCellProps);
						return element;
					}
				}
			};
		}

		static _getRenderInfo({ rowIndex, colIndex, gridInfo, isRowEdge }) {
			const crosstabGrid = gridInfo.crosstabGrid;
			if(isRowEdge) {
				//Crosstab Column headers (Constants.EDGE_TYPE.ROW)
				return {
					cellKey: `${rowIndex} ${colIndex + gridInfo.fixedColumns}`,
					isHiddenLane: (cellInfo) => CrosstabCellRenderers._isHiddenLane(isRowEdge, cellInfo, crosstabGrid),
					hiddenZIndex: gridInfo.numColumns - colIndex,
					deleteSize: 'width',
					hiddenLanePosition: 'left',
					isInVisibleRange: (cellInfo, gridInfo) => gridInfo.visibleRanges &&  gridInfo.visibleRanges.colEnd >= cellInfo.colIndex,
					getOriginalCoords: (rootCoords, gridInfo) => {
						return { row: rootCoords.row, col: rootCoords.col - gridInfo.fixedColumns };
					},
					isOutVisibleRange: (cellInfo, gridInfo) => gridInfo.visibleRanges && gridInfo.visibleRanges.colEnd <= cellInfo.colIndex,
					containerClassName: 'grid-fixed-row-cell',
					offSetParent: 'ba-common-grid__top-right-grid',
					skipBoundOfColumnResizer: false,
					skipBoundOfRowResizer: true
				};
			}
			//Crosstab row headers (grid contol fixed columns)
			return {
				cellKey: `${rowIndex + gridInfo.fixedRows} ${colIndex}`,
				isHiddenLane: (cellInfo) => CrosstabCellRenderers._isHiddenLane(isRowEdge, cellInfo, crosstabGrid),
				hiddenZIndex: gridInfo.numRows - rowIndex,
				deleteSize: 'height',
				hiddenLanePosition: 'bottom',
				isInVisibleRange: (cellInfo, gridInfo) => gridInfo.visibleRanges && gridInfo.visibleRanges.rowEnd >= cellInfo.rowIndex,
				getOriginalCoords: (rootCoords, gridInfo) => {
					return { row: rootCoords.row - gridInfo.fixedRows, col: rootCoords.col };
				},
				isOutVisibleRange: (cellInfo, gridInfo) => gridInfo.visibleRanges && gridInfo.visibleRanges.rowEnd <= cellInfo.rowIndex,
				containerClassName: 'grid-fixed-column-cell',
				offSetParent: 'ba-common-grid__bottom-left-grid',
				skipBoundOfColumnResizer: true,
				skipBoundOfRowResizer: false
			};
		}

		static _renderGridDropZones({ children, isRowEdge, cellInfo, gridInfo }) {
			if(isRowEdge){
				if (cellInfo.isMeasure) {
					children.push(React.createElement('div', { class: 'grid-drop-zone left' }, ''));
					children.push(React.createElement('div', { class: 'grid-drop-zone right' }, ''));
				} else {
					// don't add the top drop zone to an expansion leaf because its root will display the drop zone
					if (!gridInfo.expandedLeafMap.has(cellInfo.currentCellKey)){
						children.push(React.createElement('div', { class: 'grid-drop-zone top' }, ''));
					}
					children.push(React.createElement('div', { class: 'grid-drop-zone bottom' }, ''));
				}
			}else{
				if (cellInfo.isMeasure) {
					children.push(React.createElement('div', { class: 'grid-drop-zone top' }, ''));
					children.push(React.createElement('div', { class: 'grid-drop-zone bottom' }, ''));
				} else {
					// don't add the left drop zone to an expansion leaf because its root will display the drop zone
					if (!gridInfo.expandedLeafMap.has(cellInfo.currentCellKey)){
						children.push(React.createElement('div', { class: 'grid-drop-zone left' }, ''));
					}
					children.push(React.createElement('div', { class: 'grid-drop-zone right' }, ''));
				}
			}
			children.push(React.createElement('div', { class: 'grid-drop-zone replace' }, ''));
		}

		static _renderExpand({ cellInfo, gridInfo, gridCellProps, isRowEdge, isInVisibleRange, getOriginalCoords, gridCellSelection }) {
			// override for expanded root cell
			if (cellInfo.expandOptions.isExpanded){
				if(isRowEdge){
					gridCellProps.style['align-items'] = 'flex-start';
				}else{
					gridCellProps.style.display = 'block';
					gridCellProps.style['align-items'] = 'normal';
				}
				gridInfo.renderedExpandedRootSet.add(cellInfo.currentCellKey);
				gridInfo.cellProps.set(cellInfo.currentCellKey, cellInfo.baseGridCellProps);
			}

			// for an expanded leaf cell
			return this._getMissingExpandedRoot(cellInfo, gridInfo, isInVisibleRange, getOriginalCoords, gridCellSelection, isRowEdge);
		}

		static _getMissingExpandedRoot(cellInfo, gridInfo, isInVisibleRange, getOriginalCoords, gridCellSelection, isRowEdge) {
			// for an expanded leaf cell
			let missingExpandedRoot = [];
			if (gridInfo.expandedLeafMap.has(cellInfo.currentCellKey)){
				const roots = gridInfo.expandedLeafMap.get(cellInfo.currentCellKey);
				if(isInVisibleRange(cellInfo, gridInfo)){
					roots.forEach((rootInfo) => {
						const root = JSON.parse(rootInfo);
						if(!gridInfo.renderedExpandedRootSet.has(root.cellKey)){
							const rootCellCoords = CrosstabCellRenderers.parseCellKey(root.cellKey);
							const rootCellKey = gridInfo.cellProps.get(root.cellKey);
							if(rootCellKey) {
								const rootCellProps = JSON.parse(rootCellKey.style);
								rootCellProps.onClick = gridInfo.cellProps.get(root.cellKey).onClick;
								const originalRootCoord = getOriginalCoords(rootCellCoords, gridInfo);
								const cellRenderer = CrosstabCellRenderers.getFixedCellRenderer({ gridInfo, isRowEdge, gridCellSelection });
								missingExpandedRoot.push(cellRenderer(originalRootCoord.row, originalRootCoord.col, rootCellProps));
								gridInfo.renderedExpandedRootSet.add(root.cellKey);
							}
						}
					});
				}
			}
			return missingExpandedRoot;
		}

		static _addResizeHandlers({ children, cellInfo, gridInfo, renderInfo }) {
			const { offSetParent, containerClassName, skipBoundOfColumnResizer, skipBoundOfRowResizer } = renderInfo;
			const crosstabGrid = gridInfo.crosstabGrid;

			// common resize props
			const commonResizeHandlerOptions = {
				cellInfo,
				gridInfo,
				dataProvider: gridInfo.dataProvider,
				containerClassName: containerClassName,
				offSetParent: offSetParent,
				rowIndex: cellInfo.currentRow,
				colIndex: cellInfo.currentCol,
				colSpan: cellInfo.colSpan,
				rowSpan: cellInfo.rowSpan,
				fixedColumns: gridInfo.fixedColumns,
				fixedRows: gridInfo.fixedRows
			};

			// column resize handlers
			children.push(crosstabGrid._renderResizeHandler(Object.assign(commonResizeHandlerOptions, {
				direction: Constants.AXIS_X,
				className: 'grid-column-resizer',
				defaultValue: gridInfo.fixedColumnWidth,
				currentValue: cellInfo.gridCellColumnWidth,
				bound: Constants.MIN_COLUMN_WIDTH,
				skipBound: skipBoundOfColumnResizer
			})));

			// row resize handlers
			children.push(crosstabGrid._renderResizeHandler(Object.assign(commonResizeHandlerOptions, {
				direction: Constants.AXIS_Y,
				className: 'grid-row-resizer',
				defaultValue: gridInfo.fixedRowHeight,
				currentValue: cellInfo.gridCellRowHeight,
				bound: Constants.MIN_ROW_HEIGHT,
				skipBound: skipBoundOfRowResizer
			})));
		}

		/**
         * @description this function captures all of the info a header cell needs to render itself
         * @param {int} rowIndex the row index of the header grid
         * @param {int} colIndex the column index of the header grid
         * @param {object} gridCellProps the cell props passed by callers
         * @param {object} gridInfo the object that has the info of a grid
         * @param {boolean} isRowEdge true for row edge (the top most row headers)
         */
		static captureFixedCellInfo(rowIndex, colIndex, gridCellProps, gridInfo, isRowEdge) {
			const cellInfo = {};
			let sizeCalculationHelper = '';
			if(isRowEdge){
				//Crosstab column headers (Fixed row headers from a grid control perspective)
				cellInfo.currentRow = rowIndex; // the row index of this cell in the whole grid
				cellInfo.currentCol = colIndex + gridInfo.fixedColumns;  //the col index of this cell includes the fixedCols to the left of it in the crosstab row headers.
				cellInfo.columnEdgeMaxRowIndex = gridInfo.fixedRows; // the row limit of the crostab column headers = the number of fixedRows in the grid
				cellInfo.rowEdgeMaxColumnIndex = gridInfo.numColumns + gridInfo.fixedColumns - 1; // the column limit of the crosstab column headers is the data limit + fixed columns in the row headers
				cellInfo.scope = 'col'; // this header will impact a whole column
				cellInfo.cellType = Constants.EDGE_TYPE.ROW;
				cellInfo.dataValue = gridInfo.dataProvider.getValue(cellInfo.currentRow, cellInfo.currentCol);
				cellInfo.isMeasure = CrosstabCellRenderers.getDataCellValue(cellInfo.dataValue, 'isMeasure');
				cellInfo.area = Constants.AREA_TYPE.COLUMN;
				cellInfo.valueType = CrosstabCellRenderers.getDataCellValue(cellInfo.dataValue, 'type');
				if (cellInfo.isMeasure && cellInfo.valueType === 'column') {
					cellInfo.valueType = 'corner';
					cellInfo.area = Constants.AREA_TYPE.MEASURE;
				}
				sizeCalculationHelper = CrosstabCellRenderers.calculateFixedRowCellSize;
			}else{
				//Crosstab row headers (Fixed column headers from a grid control perspective)
				cellInfo.currentRow = rowIndex + gridInfo.fixedRows; //the row index of this cell includes the fixedRows above it in the crosstab column headers.
				cellInfo.currentCol = colIndex; // the col index of this cell in the whole grid
				cellInfo.columnEdgeMaxRowIndex = gridInfo.numRows + gridInfo.fixedRows - 1; // the row limitation
				cellInfo.rowEdgeMaxColumnIndex = gridInfo.fixedColumns; // the column limitation
				cellInfo.scope = Constants.EDGE_TYPE.ROW;
				cellInfo.dataValue = gridInfo.dataProvider.getValue(cellInfo.currentRow, cellInfo.currentCol);
				cellInfo.isMeasure = CrosstabCellRenderers.getDataCellValue(cellInfo.dataValue, 'isMeasure');
				cellInfo.cellType = Constants.EDGE_TYPE.COLUMN;
				cellInfo.area = Constants.AREA_TYPE.ROW;
				cellInfo.valueType = CrosstabCellRenderers.getDataCellValue(cellInfo.dataValue, 'type');
				if (cellInfo.isMeasure && cellInfo.valueType === Constants.EDGE_TYPE.ROW) {
					//For nested row measures, follow the same logic used for nested column measures (see above)
					//(effectively make them value style to produce measure-oriented actions like filter and summarize
					//(not the ones for the parent tuple like keep and exclude)
					cellInfo.valueType = 'corner';
					cellInfo.area = Constants.AREA_TYPE.MEASURE;
				}
				// @todo For 11.2.1 Release remove the need to call CrosstabCellRenderers.calculateFixedColumnCellSize
				// Instead set css as .dashboard-common-grid {
				//		.dashboard-grid-cell {
				//			box-sizing: border-box;
				//		}
				// 	}
				// Since this function deduct 1 px for width and 1 px for height and there is no need to do this if use
				// box-sizing: border-box
				sizeCalculationHelper = CrosstabCellRenderers.calculateFixedColumnCellSize;
			}
			cellInfo.rowIndex = rowIndex;
			cellInfo.colIndex = colIndex;
			cellInfo.baseGridCellProps = {
				style: JSON.stringify(gridCellProps),
				onClick: gridCellProps.onClick
			};
			cellInfo.currentCellKey = `${cellInfo.currentRow} ${cellInfo.currentCol}`;
			cellInfo.dataValue = cellInfo.dataValue || gridInfo.dataProvider.getValue(cellInfo.currentRow, cellInfo.currentCol);
			cellInfo.resizeKey = CrosstabCellRenderers.buildResizeKey(cellInfo.dataValue['ancestor_or_self']);
			cellInfo.isSummary = CrosstabCellRenderers.isSummary(cellInfo.dataValue);
			cellInfo.useValue = CrosstabCellRenderers.getDataCellValue(cellInfo.dataValue, 'useValue');
			cellInfo.valueType = cellInfo.valueType || CrosstabCellRenderers.getDataCellValue(cellInfo.dataValue, 'type');
			cellInfo.dataCellValue = CrosstabCellRenderers.getDataCellValue(cellInfo.dataValue);
			cellInfo.nestLevel = CrosstabCellRenderers.getDataCellValue(cellInfo.dataValue, 'nestlevel');
			const spanInfo = GridCellColRowSpanHelper.getSpanInfo({
				gridInfo,
				cellInfo,
				gridCellProps,
				crosstabGridCellRenderers: CrosstabCellRenderers,
				handlers: {
					getColumnWidth: gridInfo.crosstabGrid.getColumnWidth.bind(gridInfo.crosstabGrid),
					getRowHeight: gridInfo.crosstabGrid.getRowHeight.bind(gridInfo.crosstabGrid)
				}
			});
			Object.assign(cellInfo, spanInfo); // colSpan, rowSpan, gridCellColimnWidth, gridCellRowheight
			CrosstabCellRenderers.updateCoveredCellSet(cellInfo, gridInfo);

			cellInfo.isCellBelowInSummaryRow = CrosstabCellRenderers.isCellBelowInSummaryRow(
				cellInfo.currentRow + cellInfo.rowSpan + 1,
				cellInfo.currentCol, gridInfo.dataProvider,
				/*fixed*/true,
				CrosstabCellRenderers.isLastSummaryMeasure(cellInfo, gridInfo));

			cellInfo.expandOptions = gridInfo.gridCellExpansionHelper.getFixedCellExpandInfo({ cellInfo, isRowEdge, gridCellProps });
			cellInfo.size = sizeCalculationHelper(cellInfo, gridInfo, gridCellProps);
			return cellInfo;
		}

		//@returns true If the parent node for this measure is a Summary
		static isSummaryMeasure(cellInfo) {
			const ancestor = cellInfo.dataValue.ancestor_or_self;
			const parentSummary = ancestor && ancestor.length > 1 && ancestor[ancestor.length - 2].isSummary;
			return !!parentSummary;
		}

		//@returns true If the parent node for this measure is a Summary AND this is the last child measure of that summary parent.
		static isLastSummaryMeasure(cellInfo, gridInfo) {
			if (CrosstabCellRenderers.isSummaryMeasure(cellInfo)) {
				const ancestorDataValue = gridInfo.dataProvider.getValue(cellInfo.currentRow, cellInfo.currentCol - 1);
				if (ancestorDataValue && cellInfo.dataValue.index===ancestorDataValue.noOfChildren-1) {
					return true;
				}
			}
			return false;
		}

		/**
         * @description render the text content of a fixed cell
         * @param {object} cellInfo
         * @param {object} gridInfo
         * @param {boolean} isRowEdge true for row edge (the topmost row headers)
         */
		static renderTextCellOfFixedCell(cellInfo, gridInfo, isRowEdge){
			const style = {};
			const textContentStyle = {};
			const classList = ['dashboard-text-cell'];
			let paddingRightClass;
			let paddingRightStyle;
			let paddingBottomClass;
			let paddingBottomStyle;
			if(isRowEdge){
				paddingRightClass = 'row-header-padding-right';
				paddingRightStyle = {
					position: 'absolute',
					left: cellInfo.size.baseWidth - 3,
					width: cellInfo.size.totalWidth - cellInfo.size.baseWidth,
					height: Constants.EXPAND_INDENTATION_HEIGHT
				};
				paddingBottomClass = 'row-header-padding-bottom';
				paddingBottomStyle = {
					position: 'absolute',
					left: 0,
					top: Constants.EXPAND_INDENTATION_HEIGHT,
					width: cellInfo.size.baseWidth,
					height: cellInfo.size.totalHeight - Constants.EXPAND_INDENTATION_HEIGHT
				};
				style['padding-top'] = 0;
				textContentStyle['min-height'] = gridInfo.crosstabGrid.textStyle.columnHeadingStyles[Constants.STYLE_CSS_MAP.FontSize];
			}else{
				paddingRightClass = 'column-header-padding-right';
				paddingRightStyle = {
					position: 'absolute',
					left: Constants.EXPAND_INDENTATION - 3,
					width: cellInfo.size.totalWidth - Constants.EXPAND_INDENTATION,
					height: cellInfo.size.baseHeight
				};
				paddingBottomClass = 'column-header-padding-bottom';
				paddingBottomStyle = {
					position: 'absolute',
					left: 0,
					top: cellInfo.size.baseHeight,
					width: Constants.EXPAND_INDENTATION,
					height: cellInfo.size.totalHeight - cellInfo.size.baseHeight
				};
				style['padding-top'] = 0;
				textContentStyle['min-height'] = gridInfo.crosstabGrid.textStyle.rowHeadingStyles[Constants.STYLE_CSS_MAP.FontSize];
			}
			let formatSpec = (cellInfo.isSummary) ? null : ((cellInfo.dataValue) ? cellInfo.dataValue.formatSpec : null);
			const formattedDisplayValue = gridInfo.isSuppressed ? Formatter.format(cellInfo.dataCellValue, formatSpec) : Formatter.formatNull(cellInfo.dataCellValue);

			const textCellChildren = [];
			if(cellInfo.expandOptions.levelNumber !== undefined){
				const expandCollapseHandler = cellInfo.expandOptions.isExpanded ? CrosstabCellRenderers.renderCollapseButton(cellInfo.expandOptions, gridInfo) : CrosstabCellRenderers.renderExpandButton(cellInfo.expandOptions, gridInfo);
				textCellChildren.push(expandCollapseHandler);
			}

			let paddingRight = null;
			let paddingBottom = null;
			style['display'] = 'flex';
			style['align-items'] = Constants.JUSTIFY_OPTIONS.Center;
			if(cellInfo.expandOptions.isExpanded){
				if(!cellInfo.expandOptions.isExpansionLineageHintSupported) {
					// When not showing nesting depth visually, use same height and padding styles
					// as non-expanded case to align text properly.
					style['height'] = '100%';
					style['padding'] = 0;
				} else {
					style['padding-bottom'] = 0;
					delete style['padding-top'];
				}
				Object.assign(textContentStyle, {
					overflow: 'hidden',
					'text-overflow': 'ellipsis'
				});
				const textContent = React.createElement('div', {
					style: textContentStyle
				}, [formattedDisplayValue]);
				textCellChildren.push(textContent);

				style['box-sizeing'] = 'border-box';
				style['flex'] = 'none';
				classList.push('expanded');

				paddingRight = React.createElement('div', {
					class: paddingRightClass,
					style: paddingRightStyle
				});
				paddingBottom = React.createElement('div', {
					class: paddingBottomClass,
					style: paddingBottomStyle
				});
				textCellChildren.push(paddingRight, paddingBottom);
			} else {
				style['height'] = '100%';
				Object.assign(textContentStyle, {
					height: '100%',
					display: 'flex',
					alignItems: Constants.JUSTIFY_OPTIONS.Center,
					overflow: 'hidden'
				});
				const textValue = <span style={{
					overflow: 'hidden',
					'text-overflow': 'ellipsis',
					'line-height': '1.2'
				}}>{formattedDisplayValue}</span>;
				const textContent = React.createElement('div', {
					style: textContentStyle
				}, [textValue]);
				textCellChildren.push(textContent);
			}
			const textCell = React.createElement('div', {
				class: classList.join(' '),
				style: style
			}, textCellChildren);
			return textCell;
		}

		/**
         * @description this function calculate the actual size of a header cell
         * @param {object} cellInfo
         * @param {object} gridInfo
         * @param {object} gridCellProps
         */
		static calculateFixedRowCellSize(cellInfo, gridInfo, gridCellProps){
			const sizeInfo = {};
			/** For the width */
			gridCellProps.style.width = cellInfo.gridCellColumnWidth;
			if (cellInfo.currentCol + cellInfo.colSpan + 1 >= gridInfo.numColumns || cellInfo.isSummary) {
				gridCellProps.style.width = gridCellProps.style.width - 2;
			}
			sizeInfo.baseWidth = gridCellProps.style.width;
			if (cellInfo.expandOptions.isExpanded && cellInfo.expandOptions.isExpansionLineageHintSupported) {
				gridCellProps.style.width += CrosstabCellRenderers.sumOfAllChildrenColumnWidths(cellInfo, gridInfo);
				if(gridInfo.crosstabGrid.columnWidthDeltas[cellInfo.resizeKey]) {
					gridCellProps.style.width += gridInfo.crosstabGrid.columnWidthDeltas[cellInfo.resizeKey];
				}
			}
			sizeInfo.totalWidth = gridCellProps.style.width;
			gridCellProps.style.width += 'px';

			/** For the height */
			gridCellProps.style.height = cellInfo.gridCellRowHeight;
			if (!cellInfo.isSummary){
				gridCellProps.style.height = Math.min(cellInfo.gridCellRowHeight, gridInfo.maxColHeight);
			}
			if (cellInfo.expandOptions.levelNumber - cellInfo.expandOptions.rootLevel > 0){
				const topOffset = (cellInfo.expandOptions.levelNumber - cellInfo.expandOptions.rootLevel) * Constants.EXPAND_INDENTATION_HEIGHT;
				gridCellProps.style.height -= topOffset;
				gridCellProps.style.top += topOffset;
			}
			sizeInfo.baseHeight = gridCellProps.style.height;
			sizeInfo.totalHeight = gridCellProps.style.height;
			gridCellProps.style.height += 'px';
			if (cellInfo.expandOptions && !cellInfo.expandOptions.isExpansionLineageHintSupported) {
				// When not showing nesting depth visually, a table is being used, so top/left
				// styles have no effect. Instead, use margin to create visual indentation.
				gridCellProps.style.marginTop = gridCellProps.style.top;
				delete gridCellProps.style.top;
			}

			return sizeInfo;
		}

		/**
		 * @description Sum of all children widths of an expanded column header cell.
		 * @param {object} cellInfo
		 * @param {object} gridInfo
		 */
		static sumOfAllChildrenColumnWidths (cellInfo, gridInfo) {
			let expandedWidth = 0;
			if (cellInfo.expandOptions.isExpanded) {
				let colIndex = cellInfo.currentCol + 1;
				const count = cellInfo.currentCol + cellInfo.expandOptions.numberOfChildren;
				for (colIndex; colIndex <= count; colIndex++) {
					expandedWidth += gridInfo.crosstabGrid.getColumnWidth(gridInfo.dataProvider, cellInfo.currentRow, colIndex, gridInfo.fixedColumnWidth);
				}
			}
			return expandedWidth;
		}

		/**
		 * @description calculate the size of a column header cell
		 * @param {*} dataValue
		 */
		static calculateFixedColumnCellSize(cellInfo, gridInfo, gridCellProps){
			const sizeInfo = {};
			/** For the width */
			// leave room for the borders
			gridCellProps.style.width = cellInfo.gridCellColumnWidth;
			if(gridCellProps.style.width){
				gridCellProps.style.width -= 1;
			}
			// if it is a child of an expanded cell
			if(cellInfo.expandOptions.levelNumber - cellInfo.expandOptions.rootLevel > 0){
				const leftOffset = (cellInfo.expandOptions.levelNumber - cellInfo.expandOptions.rootLevel) * Constants.EXPAND_INDENTATION;
				gridCellProps.style.width -= leftOffset;
				gridCellProps.style.left += leftOffset;
			}
			sizeInfo.baseWidth = gridCellProps.style.width;
			sizeInfo.totalWidth = gridCellProps.style.width;
			gridCellProps.style.width += 'px';
			if (cellInfo.expandOptions && !cellInfo.expandOptions.isExpansionLineageHintSupported) {
				// When not showing nesting depth visually, a table is being used, so top/left
				// styles have no effect. Instead, use margin to create visual indentation.
				gridCellProps.style.marginLeft = gridCellProps.style.left;
				delete gridCellProps.style.left;
			}

			/** For the height */
			gridCellProps.style.height = Math.min(cellInfo.gridCellRowHeight, gridInfo.maxColHeight) - 1;
			if(cellInfo.isSummary || CrosstabCellRenderers.isLastSummaryMeasure(cellInfo, gridInfo)) {
				//If this cell is a summary or the last measure nested under a summary, we need to reduce the height to accommodate
				//a bottom border UNLESS the cell below it is a summary row as well (if it is, the row below already draws the border.
				if(!cellInfo.isCellBelowInSummaryRow){
					gridCellProps.style.height -= 1; // leave 1px for the bottom border
				}
			}
			// for the most bottom column header
			if (cellInfo.currentRow + cellInfo.rowSpan + 1 >= gridInfo.dataProvider.getNumRows()) {
				gridCellProps.style.height -= 1;
			}
			sizeInfo.baseHeight = gridCellProps.style.height;

			// if it is an expanded cell that visually 'contains' its descendants to indicate
			// nesting (i.e. the expansion lineage hint)
			if (cellInfo.expandOptions.isExpanded && cellInfo.expandOptions.isExpansionLineageHintSupported) {
				gridCellProps.numberOfChildren = cellInfo.expandOptions.numberOfChildren;
				gridCellProps.style.height += cellInfo.expandOptions.numberOfChildren * gridInfo.fixedRowHeight;
				if(gridInfo.crosstabGrid.rowHeightDeltas[cellInfo.resizeKey]) {
					gridCellProps.style.height += gridInfo.crosstabGrid.rowHeightDeltas[cellInfo.resizeKey];
				}
			}
			sizeInfo.totalHeight = gridCellProps.style.height;
			gridCellProps.style.height += 'px';

			return sizeInfo;
		}

		/**
         * @description find if a cell is covered by other cell. If yes, add it to the set.
         * @param {object} cellInfo
         * @param {object} gridInfo
         */
		static updateCoveredCellSet(cellInfo, gridInfo){
			for (let row = 0; row <= cellInfo.rowSpan; row++) {
				for (let col = 0; col <= cellInfo.colSpan; col++) {
					if (row !== 0 || col !== 0) {
						gridInfo.coveredCellSet.add(`${cellInfo.currentRow + row} ${cellInfo.currentCol + col}`);
					}
				}
			}
		}

		/**
		 * @description Tell if the cell below current cell is a row-summary cell
		 * @param cellBelowRow the row of the cell going to be checked
		 * @param cellBelowCol the col of the cell going to be checked
		 * @param dataProvider the data provider
		 * @param fixedColumn if the call comes from a fixed column header render (ie: the crosstab row header section)
		 * @param [isLastSummaryMeasure] true if this measure is the last measure nested under a summary cell
		 * @example for detecting 'isCellBelowInSummaryRow' for a nested measure under a summary (measures as rows case)
		 * 			Canada	Ontario		Cost
		 * 								Revenue
		 * 			Canada	Summary		Cost
		 * 								Revenue
		 * 			USA		New York	Cost
		 * 								Revenue
		 *			USA		Summary		Cost
		 *								Revenue
		 *			Summary	(Summary)	Cost
		 *								Revenue
		 *			In this example, these rows would return true.
		 *			Canada, Ontario, Revenue returns true because Canada,Summary,Cost is also a summary
		 *			USA, New York, Revenue returns true because USA, Summary, Cost is also a summary
		 *			USA, Summary, Revenue returns true because Summary, (Summary), Cost is also a summary
		 *			BUT
		 *			Canada, Summary, Revenue returns false because USA, New York, Cost is NOT a summary
		 */
		static isCellBelowInSummaryRow(cellBelowRow, cellBelowCol, dataProvider, fixedColumns, isLastSummaryMeasure=false){
			let result;
			if(dataProvider.getNumRows() > cellBelowRow){
				const dataValue = dataProvider.getValue(cellBelowRow, cellBelowCol);
				if (fixedColumns) {
					result = CrosstabCellRenderers.isSummary(dataValue);
					//showValuesAsRows: If this cell is the last measure nested under a summary
					//AND the 'row below' (cellBelowRow) has a column to its left which is also a summary, isCellBelowInSummaryRow should also return true.
					result = result || (isLastSummaryMeasure && CrosstabCellRenderers.isSummary(dataProvider.getValue(cellBelowRow, cellBelowCol - 1)));
				} else {
					result = CrosstabCellRenderers.isInSummaryRow(dataValue);
				}
			}else{
				result = false;
			}
			return result;
		}

		static isSummary(dataValue){
			if ( dataValue ) {
				if ( dataValue.rowEdge && dataValue.columnEdge ) {
					return dataValue.columnEdge.isSummary || dataValue.rowEdge.isSummary;
				} else {
					return dataValue.isSummary;
				}
			}
			return false;
		}

		static isInSummaryRow(dataValue){
			return dataValue && dataValue.rowEdge && dataValue.rowEdge.isSummary;
		}

		static _buildFixedSectionClassList(cellInfo, gridInfo, gridCellProps, isRowEdge){
			const classList = gridCellProps.className ? [gridCellProps.className] : [];
			classList.push('dashboard-grid-cell');
			if(isRowEdge){
				this._addGridFixedRowStylesToClassList(cellInfo, gridInfo, classList);
			}else{
				this._addGridFixedColumnStylesToClassList(cellInfo, gridInfo, classList);
			}
			return classList;
		}

		static _addGridCornerStylesToClassList(cellInfo, gridInfo, classList) {
			if(cellInfo.valueType && cellInfo.valueType === 'corner'){
				// highlight the measure header cell if multiselected for calculation
				if (gridInfo.measureHeaderDecorations.indexOf(cellInfo.useValue) > -1){
					classList.push('dashboard-grid-cell-selected');
				}
			}
		}

		static _addGridFixedRowStylesToClassList(cellInfo, gridInfo, classList) {
			classList.push('grid-fixed-row-cell');
			this._addGridCornerStylesToClassList(cellInfo, gridInfo, classList);

			if(cellInfo.isSummary){
				classList.push(Constants.AREA_TYPE.SUMMARY);
				if(gridInfo.isSummaryHighlighted){
					classList.push('dashboard-grid-cell-selected');
				}
			}
		}

		static _addGridFixedColumnStylesToClassList(cellInfo, gridInfo, classList) {
			classList.push('grid-fixed-column-cell');
			this._addGridCornerStylesToClassList(cellInfo, gridInfo, classList);

			if (cellInfo.isCellBelowInSummaryRow && cellInfo.isSummary) {
				classList.push('above-summary-row');
			}

			if ( cellInfo.isSummary || CrosstabCellRenderers.isSummaryMeasure(cellInfo)) {
				if (cellInfo.isSummary) {
					classList.push(Constants.AREA_TYPE.SUMMARY, 'summary-row');
				} else {
					classList.push('summary-measure-cell');
				}
				classList.push('summary-row-top-border');	//Whether its a summary cell or a summary measure, we want the top border.

				//If this cell is a summary or the last measure nested under a summary,
				//we need to draw a bottom border to 'close this summary off'
				//UNLESS the cell below it is also in a summary row (if it is, the row below already draws a border above itself).
				if(!cellInfo.isCellBelowInSummaryRow || cellInfo.columnEdgeMaxRowIndex >= gridInfo.numRows) {
					classList.push('summary-row-bottom-border');
				}
				if(gridInfo.isSummaryHighlighted){
					classList.push('dashboard-grid-cell-selected');
				}
			}
		}

		/**
		 * @param {string} cellKey a string in a format `${currentRow} ${currentCol}`
		 * @returns {object} an object in a shape of { row: int, col: int }
		 */
		static parseCellKey(cellKey) {
			const keys = cellKey.split(' ');
			const row = parseInt(keys[0]);
			const col = parseInt(keys[1]);
			return { row, col };
		}

		/**
		 * render the hidden rows or columns handler
		 * @param {string} useValue
		 * @param {string} position
		 */
		static renderHiddenRowsAndColumnsHandler(useValue, position = 'bottom', gridInfo) {
			return React.createElement( 'div', {
				class: 'grid-hidden-rowsAndColumns ' + position,
				onDoubleClick: () => {
					gridInfo.content.setPropertyValue(Constants.PROP_ID_HIDDEN_ROWS_AND_COLS, gridInfo.hiddenRowsAndColumns.filter(hiddenRowOrColumn => hiddenRowOrColumn !== useValue));
				}
			}, '' );
		}

		static getDataCellValue(dataCellValue, valueType) {
			const getValue = (value) => {
				if (value === null || value === undefined) {
					return;
				}
				return value.value && (typeof value.value.label !== 'undefined') ? value.value.label : value.value;
			};
			const getUseValue = (value) => {
				if (value && value.type && (value.type === Constants.EDGE_TYPE.COLUMN || value.type === Constants.EDGE_TYPE.ROW)) {
					const aTuples = value.ancestor_or_self;
					if (aTuples) {
						return _.pluck(aTuples, 'value') + '';
					}
				}
				return null;
			};
			const getUseValueId = (value) => {
				if (value && value.type && (value.type === Constants.EDGE_TYPE.COLUMN || value.type === Constants.EDGE_TYPE.ROW)) {
					const aTuples = value.ancestor_or_self;
					if (aTuples) {
						return _.pluck(aTuples, 'uId') + '';
					}
				}
				return null;
			};
			if (valueType === 'tuple') {
				dataCellValue = dataCellValue && dataCellValue.tuple;
			} else if (valueType === 'useValue') {
				dataCellValue = dataCellValue && dataCellValue.isMeasure ? getValue(dataCellValue) : getUseValue(dataCellValue);
			} else if (valueType === 'uId') {
				dataCellValue = dataCellValue && dataCellValue.isMeasure ? getValue(dataCellValue) : getUseValueId(dataCellValue);
			} else if (valueType === 'condValue') {
				dataCellValue = dataCellValue && dataCellValue.condValue;
			} else if (valueType === 'type') {
				dataCellValue = dataCellValue && dataCellValue.type;
			} else if (valueType === 'isMeasure') {
				dataCellValue = dataCellValue && dataCellValue.isMeasure;
			} else if (valueType === 'nestlevel') {
				dataCellValue = dataCellValue && dataCellValue.nestlevel;
			} else if (valueType === 'rowEdge') {
				dataCellValue = dataCellValue && dataCellValue.rowEdge;
			} else if (valueType === 'columnEdge') {
				dataCellValue = dataCellValue && dataCellValue.columnEdge;
			} else if (valueType === 'ancestor_or_self') {
				dataCellValue = dataCellValue && dataCellValue.ancestor_or_self;
			} else if (valueType === 'levelNumber') {
				dataCellValue = dataCellValue && dataCellValue.value && dataCellValue.value.ln;
			} else if (valueType === 'parentUseValue') {
				dataCellValue = dataCellValue && dataCellValue.value && dataCellValue.value.pu;
			} else if (valueType === 'puId') {
				dataCellValue = dataCellValue && dataCellValue.value && dataCellValue.value.puId;
			} else {
				const value = getValue(dataCellValue);
				// Should preserve null and 0 values, except undefined. Should allow user format the 3 types of
				// cells respectively.
				dataCellValue = value === undefined ? '' : value;
			}
			return dataCellValue;
		}

		/**
		 * render the expand button
		 * @param {object} expandOptions
		 */
		static renderExpandButton(expandOptions, gridInfo) {
			const { dataItemInfo, parentUseValue, currentUseValue } = expandOptions;
			const expandCollapseFeature = gridInfo.content && gridInfo.content.getFeature('ExpandCollapse');
			return React.createElement( Toolkit.Button, {
				icon: gridInfo.crosstabGrid.iconsFeature.getIcon('add16'),
				iconSize: 'small',
				variant: 'icon',
				intent: 'primary',
				title: StringResources.get('expandBtnLabel'),
				hasIconOnly: false,
				onClick: (event) => {
					expandCollapseFeature.expand(dataItemInfo.dataitemUniqueId, parentUseValue, currentUseValue);
					event.stopPropagation();
				}
			});
		}

		/**
		 * render the collapse button
		 * @param {object} expandOptions
		 */
		static renderCollapseButton(expandOptions, gridInfo) {
			const { dataItemInfo, parentUseValue, currentUseValue, hasChildren } = expandOptions;
			const expandCollapseFeature = gridInfo.content && gridInfo.content.getFeature('ExpandCollapse');
			return React.createElement( Toolkit.Button, {
				icon: gridInfo.crosstabGrid.iconsFeature.getIcon('subtract'),
				iconSize: 'small',
				variant: 'icon',
				intent: 'primary',
				title: StringResources.get('collapseBtnLabel'),
				hasIconOnly: false,
				onClick: (event) => {
					expandCollapseFeature.collapse(dataItemInfo.dataitemUniqueId, parentUseValue, currentUseValue);
					event.stopPropagation();
				},
				style: hasChildren ? {} : { visibility: 'hidden' }
			});
		}

		/**
		 * Build resize key for the resize map from ancestor_or_self info
		 */
		static buildResizeKey(ancestor_or_self){
			let resizeKey = '';
			_.each(ancestor_or_self, dataItem => {
				if(dataItem.value){
					resizeKey += dataItem.value + ',';
				} else {
					resizeKey += dataItem + ',';
				}
			});
			return resizeKey.slice(0, -1);
		}

		/**
		 * get cached dataItemInfo by index
		 * @param {Object} cachedDataItemInfo
		 * @param {integer} row/column index
		 * @param {string} propertyName
		 * @returns {Object} dataItemInfo
		 */
		static getCachedDataItemInfoByIndex(cachedDataItemInfoMap, index, propertyName) {
			const cachedDataItemInfo = cachedDataItemInfoMap && cachedDataItemInfoMap[index];
			return propertyName ? cachedDataItemInfo && cachedDataItemInfo[propertyName] : cachedDataItemInfo;
		}

		static setSummaryColor(gridCellProps, gridInfo) {
			const summaryColor = gridInfo.conditionalFormatting && gridInfo.conditionalFormatting.getSummaryFillColor();
			if (summaryColor) {
				_.extend(gridCellProps.style, { 'background-color': summaryColor });
			}
		}

		// Tries to determine more accurately if a row (columns in the row) should be hidden. The crosstabGrid._cacheHidden* only specifies the row or column index
		// but it is used against each cell in the row/column so for parent cells, they were being hidden. For cellInfo.gridCellRowHeight and gridCellColumnWidth, the floor rounding
		// is needed cause those values get crosstab HIDDEN_ROW_HEIGHT added to them from crosstab._getSpanIndex()
		// NOTE: this hidden row algorithm in CrosstabGrid using HIDDEN_ROW_HEIGHT should be cleaned up
		static _isHiddenLane(isRowEdge, cellInfo, crosstabGrid) {
			if (isRowEdge) {
				return crosstabGrid._cachedHiddenCol[cellInfo.currentCol] && (cellInfo.gridCellColumnWidth === 0 ? true : (Math.floor(cellInfo.gridCellColumnWidth) % crosstabGrid.fixedColumnWidth !== 0));
			}
			return crosstabGrid._cachedHiddenRow[cellInfo.currentRow] && (cellInfo.gridCellRowHeight === 0 ? true : (Math.floor(cellInfo.gridCellRowHeight) % crosstabGrid.fixedRowHeight !== 0));
		}

	}
	return CrosstabCellRenderers;
});
